// Hubstaff-style activity tracker for the web client.
//
// Phase 6 rewrite (Tracker module). Highlights vs the previous version:
//   - Uses elapsed wall-clock time per tick instead of trusting
//     setInterval fidelity. Backgrounded tabs throttle setInterval to
//     ~1/min (or stop it on mobile), so the old code under-counted
//     hours when the user worked in another tab. We now compute the
//     real elapsedMs between ticks and derive active/idle seconds from
//     it — capped at 60s per tick.
//   - Listens for `online` / `offline` and buffers heartbeats to
//     localStorage when offline, then drains the queue on reconnect.
//   - Triggers an immediate tick on `visibilitychange → visible` to
//     bridge any time the throttled interval missed.
//   - Co-ordinates with LiveKit voice. When the user is in a call we
//     stamp `inCall: true` on the heartbeat so the server can bucket
//     "in-call" seconds separately from regular active time.
//   - Native-first: when running inside Electron the OS-level tracker
//     owns heartbeats. The web layer bails out of `startActivityTracker`
//     to avoid double-counting. The widget reads /tracker/widget-state
//     for the unified view either way.
//
// SCOPE: web only. Screenshots and OS-level app/window tracking live in
// the Electron desktop agent (see memory: feedback_screenshots_electron_only).

import api, { timeApi } from './api'
import { useLivekitStore } from '../stores/livekitStore'
import { getTrackerCurrentTask } from './trackerCurrentTask'

// Map the current OS route to a human "section" label, sent as the heartbeat's
// app_name so admin reports can show which part of the OS the user spent time in
// (the browser can't see external desktop apps — that's the Electron agent).
const OS_SECTIONS: Record<string, string> = {
  channels: 'Spaces', spaces: 'Spaces', mail: 'Mail', calendar: 'Calendar',
  tracker: 'Tracker', tasks: 'Tasks', taskboards: 'Tasks', notes: 'Notes',
  drive: 'Files', files: 'Files', admin: 'Admin', workspace: 'Workspace',
  knowledge: 'Knowledge', learning: 'Learning', crm: 'CRM', sign: 'Sign',
}
export function osSectionFromPath(pathname: string): string {
  const seg = pathname.replace(/^\/+/, '').split('/')[0] ?? ''
  if (!seg) return 'OS'
  return OS_SECTIONS[seg] ?? seg.charAt(0).toUpperCase() + seg.slice(1)
}

const HEARTBEAT_MS = 60_000        // 1 minute
const IDLE_THRESHOLD_MS = 60_000   // 1 minute of no input ⇒ idle
const QUEUE_KEY = 'tracker:offline-queue'
const QUEUE_MAX = 1000             // ≈16h of buffered heartbeats

// Heartbeat payload carried across both the legacy /time/heartbeat leg
// and the Tracker /v1/tracker/ingest/activity leg.
interface Heartbeat {
  // Wall-clock timestamp the tick fired at (ms since epoch). Legacy
  // heartbeat ignores this; the Tracker ingest leg uses it for the
  // minute bucket.
  timestamp: number
  keystrokes: number
  mouseClicks: number
  mouseMoves: number
  scrollEvents: number
  // Both clamped to ≤60.
  activeSeconds: number
  idleSeconds: number
  activeUrl: string
  // Whether the user was in a LiveKit voice room when this tick fired.
  // Drives the todayInCallSec bucket on /tracker/widget-state.
  inCall: boolean
  // 'native-web' = Electron-attributed heartbeat synthesised by the
  // desktop agent; 'web' = browser-only. Used by the server to detect
  // dual-source conflicts (web + native both reporting → flag in
  // widget-state.sourceConflict).
  source: 'native-web' | 'web'
  // OS section (Mail/Tasks/…) derived from the route at tick time → app_name.
  appName: string
  // Taskboard item id the user chose to track into (cuid) → task_assignment_id.
  taskAssignmentId: string
}

interface Counters {
  keystrokes: number
  mouseClicks: number
  mouseMoves: number
  scrollEvents: number
  activeMs: number
}

let counters: Counters = { keystrokes: 0, mouseClicks: 0, mouseMoves: 0, scrollEvents: 0, activeMs: 0 }
let lastInputTs = Date.now()
let lastTickTs = Date.now()
let intervalId: ReturnType<typeof setInterval> | null = null
let initialized = false

// Throttled mouse move counter — moves can fire 100+/sec, we don't need
// every one.
let lastMoveSampleTs = 0
const MOVE_SAMPLE_MS = 50

// --- Offline buffer (localStorage; IDB upgrade is a Phase 7 candidate) -----

function readQueue(): Heartbeat[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as Heartbeat[]) : []
  } catch {
    return []
  }
}

function writeQueue(q: Heartbeat[]): void {
  try {
    // Keep only the last QUEUE_MAX entries — beyond that we'd risk
    // localStorage quota errors and the oldest data is least useful
    // (server caps backfill anyway).
    const trimmed = q.length > QUEUE_MAX ? q.slice(-QUEUE_MAX) : q
    localStorage.setItem(QUEUE_KEY, JSON.stringify(trimmed))
  } catch {
    // Quota exceeded or storage disabled — drop silently.
  }
}

function toLegacyPayload(hb: Heartbeat) {
  return {
    keystrokes: hb.keystrokes,
    mouseClicks: hb.mouseClicks,
    mouseMoves: hb.mouseMoves,
    scrollEvents: hb.scrollEvents,
    activeSeconds: hb.activeSeconds,
    idleSeconds: hb.idleSeconds,
    activeUrl: hb.activeUrl,
    source: hb.source,
    inCall: hb.inCall,
  }
}

function toIngestPayload(hb: Heartbeat) {
  return {
    minuteAt: new Date(hb.timestamp).toISOString(),
    activeSeconds: hb.activeSeconds,
    idleSeconds: hb.idleSeconds,
    keystrokes: hb.keystrokes,
    mouseClicks: hb.mouseClicks,
    mouseMoves: hb.mouseMoves,
    scrollEvents: hb.scrollEvents,
    client: hb.source,
    inCall: hb.inCall,
    appName: hb.appName,
    activeUrl: hb.activeUrl,
    ...(hb.taskAssignmentId ? { taskAssignmentId: hb.taskAssignmentId } : {}),
  }
}

async function flushQueue(): Promise<void> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return
  const q = readQueue()
  if (q.length === 0) return
  const remaining: Heartbeat[] = []
  for (const hb of q) {
    try {
      await timeApi.heartbeat(toLegacyPayload(hb))
      // Best-effort second leg — same logic as the live tick.
      try {
        await api.post('/v1/tracker/ingest/activity', toIngestPayload(hb))
      } catch {
        // Ignore — the legacy leg already recorded the work.
      }
    } catch {
      remaining.push(hb)
    }
  }
  writeQueue(remaining)
}

// --- Input listeners --------------------------------------------------------

function recordInput(now: number, delta: Partial<Counters> = {}) {
  // Bridge active time: count the gap since the last input as active
  // (capped to IDLE_THRESHOLD_MS so a long gap doesn't register hours).
  const dt = Math.min(now - lastInputTs, IDLE_THRESHOLD_MS)
  if (dt > 0 && dt < IDLE_THRESHOLD_MS) {
    counters.activeMs += dt
  }
  lastInputTs = now
  if (delta.keystrokes) counters.keystrokes += delta.keystrokes
  if (delta.mouseClicks) counters.mouseClicks += delta.mouseClicks
  if (delta.mouseMoves) counters.mouseMoves += delta.mouseMoves
  if (delta.scrollEvents) counters.scrollEvents += delta.scrollEvents
}

function onKeyDown(_e: KeyboardEvent) {
  // Don't capture key contents — only the count.
  recordInput(Date.now(), { keystrokes: 1 })
}

function onClick(_e: MouseEvent) {
  recordInput(Date.now(), { mouseClicks: 1 })
}

function onMouseMove(_e: MouseEvent) {
  const now = Date.now()
  if (now - lastMoveSampleTs < MOVE_SAMPLE_MS) {
    lastInputTs = now
    return
  }
  lastMoveSampleTs = now
  recordInput(now, { mouseMoves: 1 })
}

function onScroll(_e: Event) {
  recordInput(Date.now(), { scrollEvents: 1 })
}

function onVisibilityChange() {
  // When the tab returns from background, fire an immediate tick so we
  // record the time the throttled interval missed. The tick computes
  // elapsedMs from the wall clock so it doesn't matter that
  // setInterval was paused — we still attribute the elapsed minutes.
  if (document.visibilityState === 'visible') {
    void flush()
  }
}

function onOnline() {
  void flushQueue()
}

function onPageHide() {
  // Best-effort drain on tab close. If the request gets cancelled the
  // data stays in localStorage and the next session picks it up.
  void flushQueue()
}

// --- Core flush -------------------------------------------------------------

function isInCall(): boolean {
  try {
    const room = useLivekitStore.getState().room
    return room !== null && room !== undefined
  } catch {
    return false
  }
}

function detectSource(): 'native-web' | 'web' {
  // The Electron preload bridge sets `electronAPI.isElectron === true`
  // (subagent C). When present, the OS-level tracker owns heartbeat
  // generation; web flushes still tag themselves so the server can
  // detect dual-source conflicts.
  const isElectron = !!(globalThis as unknown as Window).electronAPI?.isElectron
  return isElectron ? 'native-web' : 'web'
}

async function flush() {
  const now = Date.now()
  const elapsedMs = Math.max(0, now - lastTickTs)
  lastTickTs = now

  // Active vs idle within this window. Cap each to 60s so a multi-hour
  // background run (e.g. laptop closed) doesn't synthesise a single
  // huge bucket — the rest is implicit idle and the server rolls it
  // up via the daily aggregator. activeMs is bounded by the input
  // events in this window; the remainder of `elapsedMs` is idle.
  const idleMs = Math.max(0, elapsedMs - counters.activeMs)
  const activeSeconds = Math.min(60, Math.round(counters.activeMs / 1000))
  const idleSeconds = Math.min(60, Math.round(idleMs / 1000))

  const hb: Heartbeat = {
    timestamp: now,
    keystrokes: counters.keystrokes,
    mouseClicks: counters.mouseClicks,
    mouseMoves: counters.mouseMoves,
    scrollEvents: counters.scrollEvents,
    activeSeconds,
    idleSeconds,
    activeUrl: typeof window !== 'undefined' ? window.location.href : '',
    inCall: isInCall(),
    source: detectSource(),
    appName: typeof window !== 'undefined' ? osSectionFromPath(window.location.pathname) : 'OS',
    taskAssignmentId: getTrackerCurrentTask()?.taskId ?? '',
  }

  // Reset for next window
  counters = { keystrokes: 0, mouseClicks: 0, mouseMoves: 0, scrollEvents: 0, activeMs: 0 }

  // Offline → buffer & bail. We stop ticking the network until the
  // browser tells us we're back online (handled by the `online` event).
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    const q = readQueue()
    q.push(hb)
    writeQueue(q)
    return
  }

  try {
    const res = await timeApi.heartbeat(toLegacyPayload(hb))
    // The server stops the running entry when a daily/weekly limit is
    // reached and tells us via `limitReached`. Refresh the timeStore so
    // the UI flips to "stopped" without waiting for the next /current poll.
    if ((res as { data?: { limitReached?: unknown } } | undefined)?.data?.limitReached) {
      const { useTimeStore } = await import('@os/tracker/web/stores/timeStore')
      await useTimeStore.getState().fetchCurrent()
    }
    // Drain any backlog we accumulated while offline.
    void flushQueue()
  } catch {
    // Network error — buffer for retry on reconnect.
    const q = readQueue()
    q.push(hb)
    writeQueue(q)
  }

  // Second leg: Tracker-module native ingest. Independent try/catch — if
  // the user has no tracker_member yet (404 before the next os-users-sync
  // tick) or the route is gated, we don't want it to mask the legacy
  // heartbeat.
  try {
    await api.post('/v1/tracker/ingest/activity', toIngestPayload(hb))
  } catch {
    // Same logic — best-effort.
  }
}

export function startActivityTracker() {
  if (initialized) return
  // When running inside Electron the native tracker owns heartbeat
  // collection — bail out to avoid double counting. The widget still
  // works (it polls /tracker/widget-state); we just don't generate
  // heartbeats from the renderer.
  const isElectron = !!(globalThis as unknown as Window).electronAPI?.isElectron
  if (isElectron) return

  initialized = true
  lastInputTs = Date.now()
  lastTickTs = Date.now()

  window.addEventListener('keydown', onKeyDown, { capture: true, passive: true })
  window.addEventListener('mousedown', onClick, { capture: true, passive: true })
  window.addEventListener('mousemove', onMouseMove, { capture: true, passive: true })
  window.addEventListener('scroll', onScroll, { capture: true, passive: true })
  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('online', onOnline)
  window.addEventListener('pagehide', onPageHide)

  intervalId = setInterval(flush, HEARTBEAT_MS)
  // Drain any backlog from a previous session immediately on startup.
  void flushQueue()
}

export function stopActivityTracker() {
  if (!initialized) return
  initialized = false
  if (intervalId) clearInterval(intervalId)
  intervalId = null
  window.removeEventListener('keydown', onKeyDown, true)
  window.removeEventListener('mousedown', onClick, true)
  window.removeEventListener('mousemove', onMouseMove, true)
  window.removeEventListener('scroll', onScroll, true)
  document.removeEventListener('visibilitychange', onVisibilityChange)
  window.removeEventListener('online', onOnline)
  window.removeEventListener('pagehide', onPageHide)
}

// Force a flush — e.g. when the user toggles their work category, so
// the bucket change is visible immediately in admin reports. Also used
// by tests.
export function flushNow() {
  return flush()
}
